import { encodeBarcode } from "./barcode";
// ESC/POS encoder built from the device-agnostic receipt model (lib/receipt.js).
const ESC = 0x1b;
const GS = 0x1d;
export const COLS = { "58mm": 32, "80mm": 48 };

const ASCII_MAP = { "—": "-", "–": "-", "·": "-", "×": "x", "’": "'", "‘": "'", "“": '"', "”": '"', "…": "...", "₨": "Rs", "•": "*" };

export function toAscii(s) {
  return String(s ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\x00-\x7f]/g, (c) => ASCII_MAP[c] ?? "?");
}

export function wrap(text, cols) {
  const words = toAscii(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (let w of words) {
    while (w.length > cols) {
      if (cur) { lines.push(cur); cur = ""; }
      lines.push(w.slice(0, cols));
      w = w.slice(cols);
    }
    if (!cur) cur = w;
    else if (cur.length + 1 + w.length <= cols) cur += " " + w;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [""];
}

export function leftRight(left, right, cols) {
  const r = toAscii(right);
  const maxLeft = cols - r.length - 1;
  const ls = wrap(left, Math.max(maxLeft, 8));
  const last = ls.pop();
  const out = [...ls];
  if (last.length + 1 + r.length <= cols) out.push(last + " ".repeat(cols - last.length - r.length) + r);
  else out.push(last, " ".repeat(Math.max(0, cols - r.length)) + r);
  return out;
}

const center = (t, cols) => wrap(t, cols).map((l) => " ".repeat(Math.floor((cols - l.length) / 2)) + l);

class Builder {
  constructor() { this.bytes = [ESC, 0x40, ESC, 0x74, 0x00]; }
  raw(...b) { this.bytes.push(...b); return this; }
  text(s) { for (const ch of toAscii(s)) this.bytes.push(ch.charCodeAt(0)); return this; }
  line(s = "") { return this.text(s).raw(0x0a); }
  lines(arr) { arr.forEach((l) => this.line(l)); return this; }
  align(n) { return this.raw(ESC, 0x61, n); }
  bold(on) { return this.raw(ESC, 0x45, on ? 1 : 0); }
  size(n) { return this.raw(GS, 0x21, n); }
  cut() { return this.raw(ESC, 0x64, 4, GS, 0x56, 0x42, 0x00); }
}

// Code 128 (set B) for ESC/POS: native GS k (function B, m=73), or a GS v 0 raster image for printers without it.
export function barcodeBytes(code, mode = "native") {
  const data = toAscii(code);
  if (mode === "native") {
    const payload = [0x7b, 0x42, ...[...data].map((c) => c.charCodeAt(0))];
    return [GS, 0x68, 80, GS, 0x77, 2, GS, 0x48, 2, GS, 0x6b, 73, payload.length, ...payload, 0x0a];
  }
  const enc = encodeBarcode(code);
  if (!enc) return [];
  const mod = 2, h = 80;
  const bits = "0".repeat(enc.quiet[0]) + enc.bits + "0".repeat(enc.quiet[1]);
  const wpx = bits.length * mod, wb = Math.ceil(wpx / 8);
  const row = new Array(wb).fill(0);
  for (let x = 0; x < wpx; x++) if (bits[Math.floor(x / mod)] === "1") row[x >> 3] |= 0x80 >> (x & 7);
  const img = [];
  for (let y = 0; y < h; y++) img.push(...row);
  return [GS, 0x76, 0x30, 0, wb & 0xff, wb >> 8, h & 0xff, h >> 8, ...img, ...[...data].map((c) => c.charCodeAt(0)), 0x0a];
}

// Returns { bytes: Uint8Array, preview: string[] } — preview mirrors the printed text lines.
export function encodeReceipt(model, barcodeMode = "native") {
  const cols = COLS[model.width] || 48;
  const b = new Builder();
  const preview = [];
  const push = (arr) => { preview.push(...arr); b.lines(arr); };
  const rule = "-".repeat(cols);

  b.align(0).bold(true).size(0x11);
  const nameLines = center(model.businessName, Math.floor(cols / 2));
  push(nameLines.map((l) => l.trim()).map((l) => " ".repeat(Math.max(0, Math.floor((cols / 2 - l.length) / 2))) + l));
  b.size(0).bold(false);
  [...model.header, model.branch, model.branchAddress, model.branchPhone, model.taxNumber].filter(Boolean).forEach((h) => push(center(h, cols)));
  if (model.title) { b.bold(true); push(center(model.title, cols)); b.bold(false); }
  push([rule]);
  push(leftRight(model.codeLabel || "Receipt", model.receiptCode, cols));
  (model.notes || []).forEach((n) => push(wrap(n, cols)));
  push(leftRight(model.date, model.time, cols));
  push([rule]);
  model.items.forEach((it) => {
    b.bold(true); push(wrap(it.name, cols)); b.bold(false);
    push(leftRight(`  ${it.qtyLine}`, it.amount, cols));
    if (it.discount) push(leftRight("  Discount", it.discount, cols));
    it.batches.forEach((x) => push(wrap(`  ${x}`, cols)));
  });
  push([rule]);
  model.totals.forEach((t) => {
    if (t.strong) { b.bold(true).size(0x01); push(leftRight(t.label, t.value, cols)); b.size(0).bold(false); }
    else push(leftRight(t.label, t.value, cols));
  });
  push([rule]);
  model.payment.forEach((p) => push(leftRight(p.label, p.value, cols)));
  if (model.barcode) {
    push([rule]);
    b.align(1).raw(...barcodeBytes(model.barcode, barcodeMode)).align(0);
    preview.push(center(`|||| CODE128 ${model.barcode} ||||`, cols)[0]);
  }
  if (model.footer.length) push([rule]);
  model.footer.forEach((f) => push(center(f, cols)));
  push(center("Powered by Smart-in Retail", cols));
  b.cut();
  return { bytes: new Uint8Array(b.bytes), preview };
}

export function encodeTestPage(width, deviceName) {
  const cols = COLS[width] || 48;
  const b = new Builder();
  b.bold(true).line(center("SMART-IN RETAIL", cols)[0]).bold(false);
  b.line(center("Printer test", cols)[0]);
  b.line("-".repeat(cols));
  b.line(leftRight("Device", deviceName || "printer", cols)[0]);
  b.line(leftRight("Width", `${width} / ${cols} cols`, cols)[0]);
  b.line("0123456789".repeat(Math.ceil(cols / 10)).slice(0, cols));
  b.line(leftRight("TOTAL", "1,234.500", cols)[0]);
  b.line("-".repeat(cols));
  b.line(center("If this fits on one line per row,", cols)[0]);
  b.line(center("your width is set correctly.", cols)[0]);
  b.cut();
  return new Uint8Array(b.bytes);
}
